export type PublicLocationShareScope = 'room' | 'drawer' | 'case';

export type PublicLocationShareSample = {
  drawerNumber: number | null;
  caseName: string | null;
};

export type PublicLocationShareGroup<T extends PublicLocationShareSample> = {
  key: string;
  label: string | null;
  samples: T[];
};

function groupKey(sample: PublicLocationShareSample): string {
  return `${sample.drawerNumber ?? 'd'}|${sample.caseName ?? ''}`;
}

/**
 * 公開共有一覧のグループ分け（room=引き出し×ケース、drawer=ケース、case=単一）。
 */
export function buildPublicLocationShareGroups<T extends PublicLocationShareSample>(
  samples: T[],
  scope: PublicLocationShareScope,
  labels: {
    unassignedDrawer: string;
    unassignedCase: string;
  }
): PublicLocationShareGroup<T>[] {
  if (scope === 'case') {
    return [{ key: 'all', label: null, samples }];
  }

  const map = new Map<string, { label: string; samples: T[] }>();
  for (const sample of samples) {
    const key = groupKey(sample);
    let label: string;
    if (scope === 'room') {
      const drawer =
        sample.drawerNumber != null ? `#${sample.drawerNumber}` : labels.unassignedDrawer;
      const caseLabel = sample.caseName?.trim() || labels.unassignedCase;
      label = `${drawer} · ${caseLabel}`;
    } else {
      label = sample.caseName?.trim() || labels.unassignedCase;
    }
    const existing = map.get(key);
    if (existing) existing.samples.push(sample);
    else map.set(key, { label, samples: [sample] });
  }

  return [...map.entries()].map(([key, value]) => ({
    key,
    label: value.label,
    samples: value.samples,
  }));
}
